'use client'

import { usePathname } from 'next/navigation'
import { navigateUrl } from '@/utils/client-request'
import { useOrderLookup } from '@/lib/commerce/hooks'
import { TextField } from '../elements/Field'

const focusRing = 'focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground'
const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'

const STEPS = [
  { title: 'Захиалгын дугаар', body: 'Имэйл болон SMS-ээр ирсэн баталгаажуулалтын захиалгын дугаарыг олно уу. Жишээ : DS25111222.' },
  { title: 'Бүртгэлтэй утас', body: 'Захиалгаа үүсгэхдээ оруулсан 8 оронтой утасны дугаараа бичнэ.' },
  { title: 'Захиалгаа харах', body: 'Хүргэлтийн явц, төлбөрийн мэдээлэл зэрэг бүх мэдээллээ авах боломжтой.' },
]

export default function OrderCheck({ notFound, initialOrderNum, initialPhoneNum }: { vendor?: any, notFound?: boolean, initialOrderNum?: string, initialPhoneNum?: string }) {
  const pathname = usePathname()
  const { orderNum, setOrderNum, phoneNum, setPhoneNum, errors, isLoading, submit } = useOrderLookup({ initialOrderNum, initialPhoneNum })

  const onKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      submit()
    }
  }

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20">
      <nav aria-label="Breadcrumb" className="flex items-center gap-x-1.5 text-xs text-muted-foreground">
        <a href={navigateUrl('/', pathname)} className={`transition-colors duration-150 hover:text-foreground ${focusRing}`}>Нүүр</a>
        <span aria-hidden>/</span>
      </nav>

      <header className="mt-8 lg:mt-16 max-w-3xl">
        <div className={eyebrow}>Захиалга</div>
        <h1 className="mt-4 text-4xl sm:text-6xl tracking-tight text-foreground">Захиалгаа шалгах</h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">Нэвтрэх шаардлагагүйгээр захиалгын дугаар болон утасны дугаараараа хүргэлтийн явц, төлбөрийн мэдээллээ хараарай.</p>
      </header>

      <section className="mt-12 lg:mt-16 max-w-3xl">
        {notFound && (
          <div role="alert" className="mb-8 border-l border-red-600 pl-4">
            <p className="text-sm text-foreground">Захиалга олдсонгүй</p>
            <p className="mt-1 text-[13px] leading-relaxed text-muted-foreground">Захиалгын дугаар болон утасны дугаараа шалгаад дахин оролдоно уу.</p>
          </div>
        )}

        <form onSubmit={e => { e.preventDefault(); submit() }} className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] gap-4 sm:items-end">
          <TextField label="Захиалгын дугаар" value={orderNum} onChange={e => setOrderNum(e.target.value)} placeholder="DS25111222" onKeyDown={onKey} autoComplete="off" />
          <TextField label="Утасны дугаар" type="tel" inputMode="numeric" value={phoneNum} onChange={e => setPhoneNum(e.target.value.replace(/\D/g, '').slice(0, 8))} placeholder="99999999" onKeyDown={onKey} autoComplete="tel" />
          <button type="submit" disabled={isLoading} className={`h-11 px-8 bg-foreground text-background text-[13px] tracking-wide transition-opacity duration-150 hover:opacity-85 disabled:opacity-50 disabled:cursor-not-allowed ${focusRing}`}>
            {isLoading ? 'Шалгаж байна…' : 'Шалгах'}
          </button>
        </form>
        {(errors.orderNum || errors.phoneNum) && (
          <p role="alert" className="mt-3 text-[13px] text-red-600">{errors.orderNum || errors.phoneNum}</p>
        )}

        <p className="mt-6 text-[13px] text-muted-foreground">
          Бүртгэлтэй бол{' '}
          <a href={navigateUrl('/login', pathname)} className={`text-foreground pb-0.5 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat bg-position-[0_100%] bg-size-[100%_1px] hover:bg-size-[0%_1px] transition-[background-size] duration-500 ease-out motion-reduce:transition-none ${focusRing}`}>нэвтэрч</a>
          {' '}бүх захиалгаа нэг дороос хараарай.
        </p>
      </section>

      <section className="mt-20 lg:mt-28">
        <h2 className="text-2xl sm:text-4xl tracking-tight">Хэрхэн ашиглах</h2>
        <ol className="mt-8 lg:mt-10 grid grid-cols-1 md:grid-cols-3 gap-x-10 gap-y-8">
          {STEPS.map((s, i) => (
            <li key={s.title} className="pt-6 border-t border-foreground">
              <div className="text-xs tabular-nums text-muted-foreground">{String(i + 1).padStart(2, '0')}</div>
              <div className="mt-3 text-base text-foreground">{s.title}</div>
              <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">{s.body}</p>
            </li>
          ))}
        </ol>
      </section>
    </div>
  )
}
